/**
 * BreakpointSwitcher — the Desktop / Tablet / Mobile segmented toggle in the
 * canvas toolbar. Sets which device the canvas authors for (SitemapInteraction
 * breakpoint), which reframes every page's width + device chrome.
 *
 * It also TEACHES the responsive model at a glance: a "Base" label names Desktop
 * as the source of truth, `›` separators read as a cascade (edits flow
 * Desktop → Tablet → Mobile), and a dot marks any smaller view that carries
 * overrides (so you can see it's diverged from the base). Icon-first, flat, to
 * sit next to "Tidy layout / Auto-arrange".
 *
 * react-strict-dom (html.* + css.create). SVG icons are web-only content passed
 * as button children (RSD has no html.svg), matching the format toolbar.
 */
import { useSyncExternalStore } from "react";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { microSpacing, radii } from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import {
  hasOverridesForBreakpoint,
  type EditableBreakpoint,
} from "@website-builder/y-grid";
import type { Breakpoint } from "./breakpoints.js";
import { useEditorContextOptional } from "./EditorContext.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";

const s = css.create({
  group: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: microSpacing.px1,
    padding: microSpacing.px1,
    borderRadius: radii.px6,
    backgroundColor: colors.bgCard,
  },
  // "Base" — names Desktop (the leftmost, widest) as the source of truth.
  baseLabel: {
    fontSize: "9px",
    fontWeight: typography.fontWeightBold,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: colors.textMuted,
    paddingLeft: microSpacing.px5,
    paddingRight: microSpacing.px3,
  },
  // `›` between segments — the cascade direction (edits flow down to smaller).
  sep: {
    fontSize: "11px",
    color: colors.border,
    paddingLeft: microSpacing.px1,
    paddingRight: microSpacing.px1,
  },
  btn: {
    position: "relative",
    width: sizes.chip,
    height: sizes.chip,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderStyle: "none",
    padding: 0,
    borderRadius: radii.px6,
    cursor: "pointer",
    backgroundColor: { default: "transparent", ":hover": colors.bgCardHover },
    color: colors.textMuted,
  },
  btnOn: {
    backgroundColor: { default: colors.bg, ":hover": colors.bg },
    color: colors.accent,
    boxShadow: shadows.sm,
  },
  // Override marker: this smaller view has diverged from the base.
  dot: {
    position: "absolute",
    top: 3,
    right: 4,
    width: sizes.indicatorDot,
    height: sizes.indicatorDot,
    borderRadius: radii.full,
    backgroundColor: colors.accent,
  },
});

const icon = (path: React.ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    width={15}
    height={15}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block" }}
    aria-hidden
  >
    {path}
  </svg>
);

const MONITOR = icon(
  <>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </>,
);
const TABLET = icon(
  <>
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M11 18h2" />
  </>,
);
const PHONE = icon(
  <>
    <rect x="7" y="3" width="10" height="18" rx="2" />
    <path d="M11 18h2" />
  </>,
);

/** Reactive: does any grid in the doc carry an override at `bp`? Drives the dot.
 *  Subscribes to whole-doc updates (cheap boolean recompute); inert with no doc. */
function useTierHasOverrides(bp: EditableBreakpoint): boolean {
  const ydoc = useEditorContextOptional()?.ydoc ?? null;
  return useSyncExternalStore(
    (cb) => {
      if (!ydoc) return () => {};
      ydoc.on("update", cb);
      return () => ydoc.off("update", cb);
    },
    () => (ydoc ? hasOverridesForBreakpoint(ydoc, bp) : false),
    () => false,
  );
}

export function BreakpointSwitcher({
  value,
  onChange,
}: {
  value: Breakpoint;
  onChange: (bp: Breakpoint) => void;
}) {
  const tabletDiverged = useTierHasOverrides("tablet");
  const mobileDiverged = useTierHasOverrides("mobile");
  return (
    <html.div style={s.group}>
      <html.span style={s.baseLabel} aria-hidden>
        Base
      </html.span>
      <html.button
        type="button"
        onClick={() => onChange("desktop")}
        aria-pressed={value === "desktop"}
        aria-label="Design for desktop (the base layout)"
        style={[s.btn, value === "desktop" && s.btnOn]}
      >
        {MONITOR}
      </html.button>
      <html.span style={s.sep} aria-hidden>
        ›
      </html.span>
      <html.button
        type="button"
        onClick={() => onChange("tablet")}
        aria-pressed={value === "tablet"}
        aria-label={
          tabletDiverged
            ? "Design for tablet (has overrides)"
            : "Design for tablet"
        }
        style={[s.btn, value === "tablet" && s.btnOn]}
      >
        {TABLET}
        {tabletDiverged && <html.span aria-hidden style={s.dot} />}
      </html.button>
      <html.span style={s.sep} aria-hidden>
        ›
      </html.span>
      <html.button
        type="button"
        onClick={() => onChange("mobile")}
        aria-pressed={value === "mobile"}
        aria-label={
          mobileDiverged
            ? "Design for mobile (has overrides)"
            : "Design for mobile"
        }
        style={[s.btn, value === "mobile" && s.btnOn]}
      >
        {PHONE}
        {mobileDiverged && <html.span aria-hidden style={s.dot} />}
      </html.button>
    </html.div>
  );
}
